import React, { useState, useEffect, useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";

// @mui material components
import CircularProgress from "@mui/material/CircularProgress";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";
import CustomOTPInput from "components/OTPInput";

// Authentication layout components
import AuthLayout from "layouts/authentication/components/AuthLayout";

// Images
import ccLogo from "assets/cc-images/form-side-img.svg";

// API
import { generateTotpQrResetApi, completeTotpResetApi } from "lib/Api/Authentication";
import { CommonContext } from "context/CommonCtx";
import { KeyboardArrowLeft } from "@mui/icons-material";

function ResetTotpCompleteView() {
    const { search } = useLocation();
    const navigate = useNavigate();
    const { CCLoader } = useContext(CommonContext);

    const [token, setToken] = useState(null);
    const [qrCode, setQrCode] = useState(null);
    const [userSecret, setUserSecret] = useState(null);
    const [email, setEmail] = useState("");
    const [error, setError] = useState(null);
    const [notification, setNotification] = useState({
        open: false,
        color: "info",
        title: "",
        content: "",
        icon: "info",
    });

    useEffect(() => {
        const queryParams = new URLSearchParams(search);
        const tokenParam = queryParams.get("token");

        if (tokenParam) {
            setToken(tokenParam);
            validateTokenAndGetQr(tokenParam);
        } else {
            setError("Invalid or missing reset token.");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search]);

    const validateTokenAndGetQr = async (resetToken) => {
        try {
            CCLoader(true);
            const response = await generateTotpQrResetApi({ token: resetToken });
            if (response?.status === 200) {
                setQrCode(response.data.data.image);
                setUserSecret(response.data.data.userSecret);
                setEmail(response.data.data.email);
            } else {
                setError(response?.data?.message || "Invalid or expired reset token.");
            }
        } catch (err) {
            setError("An error occurred while validating the token.");
        } finally {
            CCLoader(false);
        }
    };

    const formik = useFormik({
        initialValues: {
            otp: "",
        },
        validationSchema: Yup.object({
            otp: Yup.string()
                .length(6, "OTP must be exactly 6 digits")
                .matches(/^\d+$/, "OTP must be digits only")
                .required("OTP is required"),
        }),
        onSubmit: async (values) => {
            try {
                CCLoader(true);
                const response = await completeTotpResetApi({
                    token,
                    secret: userSecret,
                    otp: values.otp,
                });

                if (response?.status === 200) {
                    setNotification({
                        open: true,
                        color: "success",
                        title: "Success",
                        content: "TOTP reset successfully. Redirecting to login...",
                        icon: "check",
                    });
                    setTimeout(() => {
                        navigate("/login");
                    }, 1000);
                } else {
                    setNotification({
                        open: true,
                        color: "error",
                        title: "Error",
                        content: response?.data?.message || "Failed to reset TOTP. Please try again.",
                        icon: "warning",
                    });
                }
            } catch (err) {
                setNotification({
                    open: true,
                    color: "error",
                    title: "Error",
                    content: "An error occurred. Please try again.",
                    icon: "warning",
                });
            } finally {
                CCLoader(false);
            }
        },
    });

    const handleCloseNotification = () => {
        setNotification({ ...notification, open: false });
    };

    if (error) {
        return (
            <AuthLayout
                title="Reset TOTP"
                description=" "
                illustration={ccLogo}
            >
                <MDBox textAlign="center">
                    <MDTypography variant="h4" fontWeight="medium" color="text" gutterBottom>
                        Verification Failed
                    </MDTypography>
                    <MDTypography variant="body2" color="text" mb={3}>
                        The TOTP reset link you used is no longer valid.
                    </MDTypography>
                    <MDTypography variant="body2" color="text" mb={3}>
                        These links expire after a short time for security. Please request a new reset link to reset your TOTP.
                    </MDTypography>

                    <MDBox mt={3} textAlign="center">
                        <MDTypography variant="button" color="text">
                            <MDTypography
                                variant="button"
                                color="primary"
                                fontWeight="medium"
                                component="button"
                                type="button"
                                onClick={() => {
                                    navigate("/login", { replace: true });
                                }}
                                sx={{
                                    background: "none",
                                    border: "none",
                                    cursor: "pointer",
                                    textDecoration: "underline",
                                }}
                            >
                                <KeyboardArrowLeft sx={{ width: "18px" }} />
                                Back to Login
                            </MDTypography>
                        </MDTypography>
                    </MDBox>
                </MDBox>
            </AuthLayout>
        );
    }

    return (
        <AuthLayout
            title="Reset TOTP"
            description={`Scan the QR code below with your authenticator app for ${email}`}
            illustration={ccLogo}
        >
            <MDBox textAlign="center">
                {!qrCode ? (
                    <MDBox display="flex" justifyContent="center" p={3}>
                        <CircularProgress />
                    </MDBox>
                ) : (
                    <>
                        <MDBox mb={3}>
                            <img src={qrCode} alt="QR Code" style={{ width: "200px", height: "200px" }} />
                        </MDBox>

                        <MDBox component="form" role="form" onSubmit={formik.handleSubmit}>
                            <MDBox mb={2}>
                                <CustomOTPInput
                                    value={formik.values.otp}
                                    onChange={(val) => formik.setFieldValue("otp", val)}
                                    numInputs={6}
                                    error={Boolean(formik.errors.otp)}
                                    helperText={formik.errors.otp}
                                />
                            </MDBox>
                            <MDBox mt={4} mb={1}>
                                <MDButton type="submit" color="primary" fullWidth>
                                    Verify & Reset
                                </MDButton>
                            </MDBox>
                        </MDBox>
                    </>
                )}
            </MDBox>
            <MDSnackbar
                color={notification.color}
                icon={notification.icon}
                title={notification.title}
                content={notification.content}
                open={notification.open}
                onClose={handleCloseNotification}
                close={handleCloseNotification}
                bgWhite
            />
        </AuthLayout>
    );
}

export default ResetTotpCompleteView;
